import React, { useEffect, useState } from "react";
import { useApiUrl, useTranslate } from "@refinedev/core";
import { Form, InputNumber, Modal, Typography, message } from "antd";
import { axiosInstance } from "../../../authProvider";

const { Text } = Typography;

type AddCreditModalProps = {
  open: boolean;
  accountId: number | null;
  accountName?: string;
  currentCredit?: number;
  onCancel: () => void;
  onSuccess: () => void;
};

type AddCreditFormValues = {
  amount: number;
};

type AddCreditResponse = {
  id: number;
  credit: number;
  previousCredit: number;
  amountAdded: number;
};

export const AddCreditModal: React.FC<AddCreditModalProps> = ({
  open,
  accountId,
  accountName,
  currentCredit = 0,
  onCancel,
  onSuccess,
}) => {
  const translate = useTranslate();
  const apiUrl = useApiUrl();
  const [form] = Form.useForm<AddCreditFormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleOk = async () => {
    if (accountId == null) {
      return;
    }
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);
      await axiosInstance.post<AddCreditResponse>(
        `${apiUrl}/accounts/${accountId}/add-credit`,
        { amount: values.amount },
      );
      message.success(translate("accounts.addCredit.success"));
      onSuccess();
      onCancel();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(
        error?.response?.data?.errors?.billingMode
          ? translate("accounts.addCredit.notPrepaid")
          : translate("accounts.addCredit.error"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={translate("accounts.addCredit.title")}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={isSubmitting}
      okText={translate("accounts.addCredit.submit")}
      destroyOnClose
    >
      <div className="mb-4 flex flex-col gap-1">
        {accountName && (
          <Text>
            {translate("accounts.fields.name")}: <Text strong>{accountName}</Text>
          </Text>
        )}
        <Text>
          {translate("accounts.addCredit.currentBalance")}:{" "}
          <Text strong>{currentCredit}</Text>
        </Text>
      </div>
      <Form form={form} layout="vertical">
        <Form.Item
          name="amount"
          label={translate("accounts.addCredit.amount")}
          rules={[
            { required: true, message: translate("accounts.addCredit.amountRequired") },
            {
              type: "number",
              min: 1,
              message: translate("accounts.addCredit.amountMin"),
            },
          ]}
        >
          <InputNumber className="w-full" min={1} precision={0} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
